import { useState } from 'react';
import { Alert, Box, Button, Divider, Link, Stack, TextField, Typography } from '@mui/material';
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthScreen, authSubmitSx } from '../components/auth/AuthScreen';
import { PasswordField } from '../components/common/PasswordField';
import { useAuth } from '../hooks/useAuth';
import { APP_VERSION } from '../config/app';
import { startLoginPushPermission } from '../push/webPush';
import { loginLandingPath } from '../utils/navigation';

export function LoginPage() {
  const { user, login, hydrating, submitting, error } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const authMessage = location.state?.authMessage;
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [localError, setLocalError] = useState(null);

  if (!hydrating && user) {
    return <Navigate to={loginLandingPath(from, user.role)} replace />;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setLocalError(null);
    const permissionWork = startLoginPushPermission();
    try {
      const loggedIn = await login(loginId.trim(), password, { permissionWork });
      if (!loggedIn?.role) {
        setLocalError('Login succeeded but user data was incomplete.');
        return;
      }
      navigate(loginLandingPath(from, loggedIn.role), { replace: true });
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Login failed');
    }
  }

  return (
    <AuthScreen hydrating={hydrating}>
      {(stageSx) => (
        <>
          <Box sx={stageSx(80)}>
            <Typography
              variant="h4"
              component="h1"
              sx={{ mb: 0.75, letterSpacing: '-0.03em', fontSize: { xs: '1.65rem', sm: '2rem' } }}
            >
              Sign in
            </Typography>
            <Typography color="text.secondary" sx={{ lineHeight: 1.55, fontSize: { xs: '0.95rem', sm: '1rem' } }}>
              Sign in to read stories, get personalized alerts, and access newsroom tools.
            </Typography>
          </Box>

          {(localError || error || authMessage) && (
            <Alert severity={localError || error ? 'error' : 'warning'}>
              {localError || error || authMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={onSubmit} sx={stageSx(140)}>
            <Stack spacing={2}>
              <TextField
                label="Email or mobile"
                type="text"
                value={loginId}
                onChange={(e) => setLoginId(e.target.value)}
                required
                fullWidth
                autoComplete="username"
                inputMode="text"
                autoCapitalize="none"
                autoCorrect="off"
                helperText="Use your email or mobile number"
              />
              <PasswordField
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                size="large"
                disabled={submitting}
                fullWidth
                sx={authSubmitSx}
              >
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </Stack>
          </Box>

          <Divider />
          <Stack spacing={1}>
            <Link
              component={RouterLink}
              to="/signup"
              state={{ from }}
              underline="hover"
              fontWeight={600}
            >
              New here? Create an account
            </Link>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
            Prasaar v{APP_VERSION}
          </Typography>
        </>
      )}
    </AuthScreen>
  );
}
